import React, { FC, useMemo } from 'react'
import { format, formatDistance } from 'date-fns'

import { EventContentArg } from '@fullcalendar/react'

import Checkbox from '../../UI/Inputs/Checkbox'
import CustomPopup from '../../UI/Popup'

import './Events.scss'
import CustomButton from '../../UI/Buttons/CustomButton'

import ExampleStory from '../../../assets/img/example/story.svg'
import { useDispatch, useSelector } from '../../../store'
import ModalLabel from '../ModalLabel'
import { push } from 'connected-react-router'
import {
  setPostsForApprovalAction,
  toggleIsOpenAprrovalModalAction
} from '../../../store/modal/approvalPost/slice'
import { TDuplicatePost } from '../../../store/modal/duplicatePost/types'
import { setPostsForDuplicate } from '../../../store/modal/duplicatePost/slice'
import {
  ATTACHMENT_TYPE,
  MEDIA_DATA_TYPE,
  POST_STATUS,
  SOCIAL_NETWORK,
  TPostAttachment
} from '../../../models/post'
import { generatePath } from 'react-router-dom'
import { AUTH_ROUTE } from '../../../pages/App/routes'
import { defineAvailablePostsActions } from '../../../models/postStatuses'
import { TExtendedPostAttachment, TPostForApproval } from '../../../store/managePost/newTypes'
import VideoPreview from '../../UI/VideoPreview'
import Popup from 'reactjs-popup'
import { OriginalSocialIcons } from '../../UI/Icons/socials'
import QuestionIcon from '../../UI/Icons/Question'
import LoadableImage, { ASC_PREVIEW_SIZE, LOADER_STYLE } from '../../UI/LoadableImage'
import { openSchedulePostModal } from '../../../store/application/slice'
import { SCHEDULE_ACTION_TYPES } from '../../../store/application/types'
import { setPostToApproveAction } from '../../../store/managePost/slice'

type ViewEventProps = {
  eventContent: EventContentArg
  selectedSchedules: any
  calendarDataRefs: any
  dragElementId: any
  setSelectedEvent: any
  project: any
}

const ViewEvent: FC<ViewEventProps> = ({
  eventContent,
  selectedSchedules,
  calendarDataRefs,
  dragElementId,
  setSelectedEvent,
  project,
  ...rest
}) => {
  const { event, isMirror } = eventContent
  const { isShowPopup } = useSelector((state) => ({
    isShowPopup: state.app.isShowPopup
  }))
  const dispatch = useDispatch()
  const checked: boolean = !!selectedSchedules.find(
    (selectedEvt: any) => selectedEvt.id === Number(event.id)
  )

  const { schedule, post } = useMemo(
    () => ({
      post: event.extendedProps?.schedule?.post,
      schedule: event.extendedProps?.schedule
    }),
    [event]
  )

  const moveToPostEdit = (social: SOCIAL_NETWORK) => {
    dispatch(
      push(
        generatePath(AUTH_ROUTE.MANAGE_POST, {
          projectId: post.project_id,
          social: social
        }) + `?postId=${post.id}`
      )
    )
  }

  const defineTypeMediaIsVideo = (attachment: TPostAttachment) =>
    attachment?.object_type === ATTACHMENT_TYPE.TYPE_VIDEO

  const toggleSendToApprovalPost = () => {
    if (!defineAvailablePostsActions.sendToApprove(post.status, post.latest_schedule)) {
      dispatch(setPostToApproveAction(post))
      dispatch(openSchedulePostModal(SCHEDULE_ACTION_TYPES.SCHEDULE_BEFORE_APPROVAL))
    } else {
      const { latest_schedule: schedule, post_socials: postSocials, ...restPost } = post
      dispatch(setPostsForApprovalAction([{ ...restPost, schedule, postSocials }]))
      dispatch(toggleIsOpenAprrovalModalAction())
    }
  }

  const toggleDuplicatePost = (id: number) => {
    const postForDuplicate: TDuplicatePost = {
      post_social_id: id,
      social_networks: []
    }
    dispatch(setPostsForDuplicate(postForDuplicate))
  }

  const renderEvent = () => {
    if (!post.post_socials.length) return null

    return (
      <div
        className='calendar-event'
        key={`calendar-event-post-${post.id}`}
        ref={(el) => {
          if (el) calendarDataRefs.current.setEventToCell(event, el?.parentNode)
        }}
      >
        <Checkbox
          type='checkbox'
          checked={checked}
          onChange={() => {
            setSelectedEvent(event)
          }}
        />
        <div className='calendar-event__time'>
          {format(event.start as Date, 'h:mm a').toLowerCase()}
        </div>
        <div className='calendar-event__type'>{schedule.type}</div>
        <div className='post-data-wrapper'>
          {post.post_socials.map((socialPost: any, i: number) => (
            <CustomPopup
              key={`post-popup-${i}`}
              keepTooltipInside='body'
              isShowPopup={isShowPopup}
              trigger={renderPostItem(socialPost)}
            >
              {eventPopup({ socialPost, schedule })}
            </CustomPopup>
          ))}
        </div>
        {isMirror && selectedSchedules.length > 1 && (
          <div className='calendar-event__quantity'>{selectedSchedules.length}</div>
        )}
      </div>
    )
  }

  const eventPopup = ({ socialPost, schedule }: any) => {
    const media = socialPost.attachments[0] as TExtendedPostAttachment
    const status = socialPost.status

    const displayStatus = socialPost.status === POST_STATUS.STATUS_PENDING_PUBLISH ?
      'Pending manual publish' : socialPost.status.charAt(0).toUpperCase() +
      socialPost.status.replaceAll('_', ' ').slice(1)

    return (
      <div className={`event-content status-${status}`}>
        <div className='event-content__time'>
          {format(new Date(schedule.date + 'T' + schedule.time), 'h:mm a')}
        </div>
        <div className='event-content__type'>
          {schedule.post.type.charAt(0).toUpperCase() +
            schedule.post.type.replace('_', ' ').slice(1)}
        </div>
        <div className='event-content__post-data'>
          <div className='event-content__post-data__preview'>
            <div className={`event-content__post-data__preview__badge`}>
              {OriginalSocialIcons[socialPost.social_network as SOCIAL_NETWORK]}
            </div>
            {defineTypeMediaIsVideo(media) ? (
              <VideoPreview
                attachment={media}
                width={130}
                className='event-content__post-data__preview__video'
              />
            ) : (
              <LoadableImage
                media={media}
                containerWidth={130}
                loaderStyle={LOADER_STYLE.LIGHT}
                attachmentSize={ASC_PREVIEW_SIZE.MEDIUM}
              />
            )}
          </div>
          <div className='post-data'>
            <div className='post-data__text-wrapper'>
              <div className='post-data__text-wrapper__socials'>
                <div className='post-data__text-wrapper__socials__name'>
                  {socialPost.social_network}
                </div>
                <div className='post-data__text-wrapper__socials__status'>
                  &#9679;{' '}
                  {displayStatus}
                  {socialPost.status === POST_STATUS.STATUS_PUBLISHING_FAILED && (
                    <Popup
                      className='failed-message'
                      on='hover'
                      position='left center'
                      arrow={false}
                      trigger={() => (
                        <div>
                          <QuestionIcon />
                        </div>
                      )}
                    >
                      <div className='failed-message'>
                        {!!socialPost.last_social_api_response ? JSON.stringify(socialPost.last_social_api_response) : 'An internal error has occurred.'}
                      </div>
                    </Popup>
                  )}
                </div>
              </div>
              <div className='post-data__text-wrapper__short-descr'>{socialPost.message}</div>
            </div>
            <div className='post-data__date-wrapper'>
              <div className='post-data__date-wrapper__date'>
                <div className='post-data__date-wrapper__date__title'>Publish date</div>
                <div className='post-data__date-wrapper__date__date'>
                  {format(new Date(schedule.date + 'T' + schedule.time), 'dd/MM/yy h:mm a')}
                </div>
              </div>
              <div className='post-data__date-wrapper__date'>
                <div className='post-data__date-wrapper__date__title'>Last update</div>
                <div className='post-data__date-wrapper__date__date'>
                  {formatDistance(new Date(socialPost.updated_at), new Date())} ago
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='event-content__actions'>
          <CustomButton className='no-wrap' onClick={() => moveToPostEdit(socialPost.social_network)} button='subtle'>
            Edit
          </CustomButton>
          {schedule.post.status === POST_STATUS.STATUS_DRAFT && (
            <CustomButton button='subtle' className='no-wrap' onClick={() => toggleSendToApprovalPost()}>
              To Approval
            </CustomButton>
          )}
          <CustomButton button='subtle' onClick={() => toggleDuplicatePost(socialPost.id)}>
            Duplicate media to other platforms
          </CustomButton>
        </div>
      </div>
    )
  }

  const renderPostItem = ({ attachments, social_network, status }: any) => {
    const media: TExtendedPostAttachment = attachments[0]
    return (
      <div className={`post-data status-${status}`}>
        <div className='post-data__post-wrapper'>
          <div className='post-data__post-wrapper__preview'>
            <LoadableImage
              media={media}
              loaderStyle={LOADER_STYLE.LIGHT}
              attachmentSize={ASC_PREVIEW_SIZE.SMALL}
            />
          </div>
          <div className={`post-data__post-wrapper__social-icon`}>
            {OriginalSocialIcons[social_network as SOCIAL_NETWORK]}
          </div>
          <div className='post-data__post-wrapper__social-name'>{social_network}</div>
        </div>
      </div>
    )
  }

  const renderLabel = () => {
    const data = {
      label: event,
      project: { id: event.extendedProps.project_id },
      date_start: event.start as Date,
      date_end: event.end || event.start as Date,
      calendarDataRefs
    }
    return <ModalLabel {...data} />
  }
  return event.extendedProps.isPost ? renderEvent() : renderLabel()
}

export default ViewEvent
